import React, { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { DescriptedLabel, Input, Password, BaseButton } from 'iebt-zing-storybook'
import { store } from 'store'
import _ from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLocation } from 'react-router-dom'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'

import { HotsiteFormMain } from '../..'
import { trans } from 'sharedKernel/i18n'
import { DROPDOWN_BRAZIL_STATES, INPUT_MASK_FORMAT, ROUTES } from 'sharedKernel/constants'
import { CaixaEmployeeRegisterActions } from '../FormActions'

import {
  handlePasswordAndConfirmPasswordEquality, 
  ifNotEmptyValidatePassword
} from '../../Register/FormActions/validations'
import { handleCaixaFormValidity, validateCaixaEmail } from '../FormActions/validations'
import { IFormFieldProperties } from '../../contracts'
import { ifNotEmptyValidateEmail, ifNotEmptyValidateMobile, isEmptyField } from '../../validations'
import { applyMaskIfNotEmpty, applyTitleOnError } from 'sharedKernel/handlers'
import { regexPatterns } from 'sharedKernel/regex'
import { passwordAcceptance } from 'components/Password/Rules'
import { FormTitle, PasswordAcceptance, SectionTitle, StepButtonsWrapper, StepTransition } from '../../Register/v1/style'
import { LoginButton } from '../../Register/FormActions/Login'
import { ICaixaEmployeeRegisterForm, getInitialCaixaEmployeeRegisterForm } from '../store'

export interface CaixaEmployeeRegisterFormProps {
  challengeId?: string
}

export const CaixaEmployeeRegisterForm = ({ challengeId }: CaixaEmployeeRegisterFormProps): JSX.Element => {
  const [registerFormState, setRegisterFormState] = useState<ICaixaEmployeeRegisterForm>(getInitialCaixaEmployeeRegisterForm())

  const [step, setStep] = useState(0)
  const [inTransition, setInTransition] = useState(false)
  const [outTransition, setOutTransition] = useState(false)
  const passwordAcceptanceText = passwordAcceptance()
  const location = useLocation()
  const navigate = useNavigate()

  const labels = {
    formTitle: trans('Cadastra-se e faça parte do TEIA'),
    contactName: trans('Nome Completo'),
    namePlaceholder: trans('Insira seu nome completo'),
    email: trans('E-mail institucional'),
    emailPlaceholder: trans('Insira seu e-mail'),
    phone: trans('Número para Contato'),
    phonePlaceholder: trans('Insira seu número para contato'),
    caixaInstitution: trans('Instituição'),
    caixaMatricula: trans('Matrícula'),
    caixaMatriculaPlaceholder: trans('Insira sua matrícula (ex: 123456)'),
    caixaDepartment: trans('Código da Unidade'),
    caixaDepartmentPlaceholder: trans('Insira o código da unidade'),
    caixaRole: trans('Cargo/Função'),
    caixaRolePlaceholder: trans('Insira seu cargo/função'),
    password: trans('Senha'),
    passwordPlaceholder: trans('Insira sua senha'),
    passwordConfirmation: trans('Confirmação de senha'),
    passwordConfirmationPlaceholder: trans('Confirme sua senha'),
    titleMessage: trans('Preencha este campo'),
    passwordAcceptance: trans('Sua senha deve conter no mínimo 8 caracteres, uma letra maiúscula, uma letra minúscula, um número, um caracter especial'),
    next: trans('Próximo'),
    back: trans('Voltar')
  }

  useEffect(() => {
    return () => {
      // Cleanup se necessário
    }
  }, [])

  const handleChange = (value, key): void => {
    setRegisterFormState(prev => {
      const newState = { ...prev }
      newState.form[key].value = value
      return handleCaixaFormValidity(newState)
    })
  }

  const handleBlurWithZeroPadding = (value, key, maxLength): void => {
    if (value && value.length === 1 && value.length < maxLength) {
      const paddedValue = value.padStart(maxLength, '0')
      handleChange(paddedValue, key)
    }
    isEmptyField(registerFormState.form[key])
    setRegisterFormState(prev => handleCaixaFormValidity(prev))
  }


  const passwordAndConfirmPasswordValidations = (currentPassword: IFormFieldProperties): boolean => {
    if (ifNotEmptyValidatePassword(currentPassword)) {
      return handlePasswordAndConfirmPasswordEquality(
        registerFormState?.form.password,
        registerFormState?.form.confirmPassword
      )
    }
    return false
  }


  const isValidSection = (section: number): boolean => {
    let isValid = true
    switch (section) {
      case 0:
        if (registerFormState.form.contactName.isInvalid || registerFormState.form.contactName.value === '') isValid = false
        if (registerFormState.form.email.isInvalid || registerFormState.form.email.value === '') isValid = false
        if (registerFormState.form.phone.isInvalid || registerFormState.form.phone.value === '') isValid = false
        break
      case 1:
        if (registerFormState.form.caixaRole.isInvalid || registerFormState.form.caixaRole.value === '') isValid = false
        if (registerFormState.form.caixaDepartment.isInvalid || registerFormState.form.caixaDepartment.value === '') isValid = false
        if (registerFormState.form.caixaMatricula.isInvalid || registerFormState.form.caixaMatricula.value === '') isValid = false
        break
      case 2:
        if (registerFormState.form.password.isInvalid || registerFormState.form.password.value === '') isValid = false
        if (registerFormState.form.confirmPassword.isInvalid || registerFormState.form.confirmPassword.value === '') isValid = false
        break
    }
    return isValid
  }

  const nextStep = (): void => {
    if (isValidSection(step)) {
      setInTransition(true)
      setTimeout(() => {
        setStep(step + 1)
        setInTransition(false)
      }, 500)
    }
  }

  const backStep = (): void => {
    setOutTransition(true)
    setTimeout(() => {
      setStep(step - 1)
      setOutTransition(false)
    }, 500)
  }

  const renderSection = (): JSX.Element => {
    switch (step) {
      case 0:
        return (
          <>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='contactName'
                  maxLength={60}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.contactName.message, labels.titleMessage)}
                  label={`${labels.contactName}`}
                  placeholder={labels.namePlaceholder}
                  value={registerFormState?.form.contactName.value}
                  isInvalid={registerFormState?.form.contactName.isInvalid}
                  notValidFieldMessage={registerFormState?.form.contactName.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'contactName')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.contactName)
                    setRegisterFormState(prev => handleCaixaFormValidity(prev))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Input
                    id='email'
                    maxLength={60}
                    isRequired={true}
                    title={applyTitleOnError(registerFormState?.form.email.message, labels.titleMessage)}
                    label={`${labels.email}`}
                    placeholder={labels.emailPlaceholder}
                    value={registerFormState?.form.email.value}
                    isInvalid={registerFormState?.form.email.isInvalid}
                    notValidFieldMessage={registerFormState?.form.email.message}
                    onChange={(event) => {
                      handleChange(event.target.value.trim(), 'email')
                    }}
                    onBlur={() => {
                      const emailValidation = validateCaixaEmail(registerFormState?.form.email.value)
                      setRegisterFormState(prev => {
                        const newState = { ...prev }
                        newState.form.email.isInvalid = !emailValidation.isValid
                        newState.form.email.message = emailValidation.message
                        return handleCaixaFormValidity(newState)
                      })
                    }}
                  ></Input>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='phone'
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.phone.message, labels.titleMessage)}
                  label={`${labels.phone}`}
                  placeholder={labels.phonePlaceholder}
                  value={registerFormState?.form.phone.value}
                  code="masked"
                  mask={applyMaskIfNotEmpty(registerFormState?.form.phone.value, INPUT_MASK_FORMAT.MOBILE)}
                  isInvalid={registerFormState?.form.phone.isInvalid}
                  notValidFieldMessage={registerFormState?.form.phone.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'phone')
                  }}
                  onBlur={() => {
                    ifNotEmptyValidateMobile(registerFormState?.form.phone)
                    setRegisterFormState(prev => handleCaixaFormValidity(prev))
                  }}
                ></Input>
              </Col>
              
            </Row>
          </>
        )
      case 1:
        return (
          <>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='caixaInstitution'
                  maxLength={100}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.caixaInstitution.message, labels.titleMessage)}
                  label={`${labels.caixaInstitution}`}
                  placeholder="Caixa Econômica Federal"
                  value={registerFormState?.form.caixaInstitution.value}
                  isInvalid={registerFormState?.form.caixaInstitution.isInvalid}
                  notValidFieldMessage={registerFormState?.form.caixaInstitution.message}
                  disabled={true}
                  onChange={(event) => {
                    handleChange(event.target.value, 'caixaInstitution')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.caixaInstitution)
                    setRegisterFormState(prev => handleCaixaFormValidity(prev))
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='caixaMatricula'
                  maxLength={6}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.caixaMatricula.message, labels.titleMessage)}
                  label={`${labels.caixaMatricula}`}
                  placeholder={labels.caixaMatriculaPlaceholder}
                  value={registerFormState?.form.caixaMatricula.value}
                  isInvalid={registerFormState?.form.caixaMatricula.isInvalid}
                  notValidFieldMessage={registerFormState?.form.caixaMatricula.message}
                  onChange={(event) => {
                    // Remove todos os caracteres que não são números
                    const numericValue = event.target.value.replace(/[^0-9]/g, '')
                    handleChange(numericValue, 'caixaMatricula')
                  }}
                  onBlur={() => {
                    handleBlurWithZeroPadding(registerFormState.form.caixaMatricula.value, 'caixaMatricula', 6)
                  }}
                />
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='caixaDepartment'
                  maxLength={4}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.caixaDepartment.message, labels.titleMessage)}
                  label={`${labels.caixaDepartment}`}
                  placeholder={labels.caixaDepartmentPlaceholder}
                  value={registerFormState?.form.caixaDepartment.value}
                  isInvalid={registerFormState?.form.caixaDepartment.isInvalid}
                  notValidFieldMessage={registerFormState?.form.caixaDepartment.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'caixaDepartment')
                  }}
                  onBlur={() => {
                    handleBlurWithZeroPadding(registerFormState.form.caixaDepartment.value, 'caixaDepartment', 4)
                  }}
                ></Input>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col lg={true}>
                <Input
                  id='caixaRole'
                  maxLength={100}
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.caixaRole.message, labels.titleMessage)}
                  label={`${labels.caixaRole}`}
                  placeholder={labels.caixaRolePlaceholder}
                  value={registerFormState?.form.caixaRole.value}
                  isInvalid={registerFormState?.form.caixaRole.isInvalid}
                  notValidFieldMessage={registerFormState?.form.caixaRole.message}
                  onChange={(event) => {
                    handleChange(event.target.value, 'caixaRole')
                  }}
                  onBlur={() => {
                    isEmptyField(registerFormState.form.caixaRole)
                    setRegisterFormState(prev => handleCaixaFormValidity(prev))
                  }}
                ></Input>
              </Col>
            </Row>
          </>
        )
      case 2:
        return (
          <>
            <Row className='my-2'>
              <Col lg={true}>
                <Row>
                  <Password
                    id='password'
                    isRequired={true}
                    title={applyTitleOnError(registerFormState?.form.password.message, labels.titleMessage)}
                    label={`${labels.password}`}
                    description={passwordAcceptanceText}
                    placeholder={labels.passwordPlaceholder}
                    value={registerFormState?.form.password.value}
                    isInvalid={registerFormState?.form.password.isInvalid}
                    notValidFieldMessage={registerFormState?.form.password.message}
                    onChange={(event) => {
                      handleChange(event.target.value.trim(), 'password')
                    }}
                    onBlur={() => {
                      passwordAndConfirmPasswordValidations(registerFormState?.form.password)
                      setRegisterFormState(prev => handleCaixaFormValidity(prev))
                    }}
                  ></Password>
                </Row>
              </Col>
              <Col lg={true}>
                <Password
                  id='confirmPassword'
                  isRequired={true}
                  title={applyTitleOnError(registerFormState?.form.confirmPassword.message, labels.titleMessage)}
                  label={`${labels.passwordConfirmation}`}
                  description={passwordAcceptanceText}
                  placeholder={labels.passwordConfirmationPlaceholder}
                  value={registerFormState?.form.confirmPassword.value}
                  isInvalid={registerFormState?.form.confirmPassword.isInvalid}
                  notValidFieldMessage={registerFormState?.form.confirmPassword.message}
                  onChange={(event) => {
                    handleChange(event.target.value.trim(), 'confirmPassword')
                  }}
                  onBlur={() => {
                    passwordAndConfirmPasswordValidations(registerFormState?.form.confirmPassword)
                    setRegisterFormState(prev => handleCaixaFormValidity(prev))
                  }}
                ></Password>
              </Col>
            </Row>
            <Row>
              <PasswordAcceptance isInvalid={registerFormState?.form.password.isInvalid}>{labels.passwordAcceptance}</PasswordAcceptance>
            </Row>
          </>
        )
      default:
        return <></>
    }
  }

  return (
    <>
      <FormTitle>{labels.formTitle}</FormTitle>
      <HotsiteFormMain formTitle=''>
        <StepTransition className={`${inTransition ? 'enter' : ''} ${outTransition ? 'exit' : ''}`}>
          <SectionTitle hasIcon={step > 0}>
            {step > 0 && (
              <a onClick={backStep} title={labels.back} className='cursor-pointer'>
                <FontAwesomeIcon className="" size='1x' icon={['fas', 'arrow-alt-circle-left']} />
              </a>
            )}
            <span>
              {step === 0 ? 'Informações Gerais' : step === 1 ? 'Informações Profissionais' : 'Segurança'}
            </span>
          </SectionTitle>
          {renderSection()}
        </StepTransition>
        <StepButtonsWrapper className='px-2 mt-2'>
          {step === 2
            ? (
              <>
                <CaixaEmployeeRegisterActions 
                  registerFormState={registerFormState}
                  setRegisterFormState={setRegisterFormState}
                  challengeId={challengeId}
                />
              </>
              )
            : (
              <BaseButton onClick={nextStep} size="big" btnContent={labels.next} variant="primary-invert" />
              )}
        </StepButtonsWrapper>
        <hr className='mt-4' />
        <LoginButton />
      </HotsiteFormMain>
    </>
  )
}
